/*
 * Copyright 2026 Sony Corporation
 */

/** What a project's thumbnail shows: the page markup of a check, and which check it came from. */
export interface CachedThumbnail {
  verificationId: string | null;
  html: string | null;
}

const PREFIX = 'jit.thumbnail.';
const memory = new Map<string, CachedThumbnail>();

/**
 * The thumbnail last shown for a project, so going back to the projects shows it at once. It lives in memory and,
 * as far as there is room, in the tab's session storage, so a reload keeps it too.
 */
export const thumbnailCache = {
  get(projectId: string): CachedThumbnail | undefined {
    const held = memory.get(projectId);
    if (held) {
      return held;
    }
    try {
      const stored = window.sessionStorage.getItem(PREFIX + projectId);
      if (stored) {
        const parsed = JSON.parse(stored) as CachedThumbnail;
        memory.set(projectId, parsed);
        return parsed;
      }
    } catch {
      // Storage unavailable or broken entry: fetch afresh.
    }
    return undefined;
  },

  set(projectId: string, entry: CachedThumbnail): void {
    memory.set(projectId, entry);
    try {
      window.sessionStorage.setItem(PREFIX + projectId, JSON.stringify(entry));
    } catch {
      // Full: the memory copy still serves this visit.
    }
  },

  clear(): void {
    memory.clear();
    try {
      Object.keys(window.sessionStorage)
        .filter((key) => key.startsWith(PREFIX))
        .forEach((key) => window.sessionStorage.removeItem(key));
    } catch {
      // Nothing to clear.
    }
  },
};
